/**
 * HuddleApi over fetch (contracts routes joinHuddle/updateHuddle/huddleHeartbeat/leaveHuddle…) with the session's
 * access token: refreshed before it expires, and once more after a 401. `leave` uses keepalive so it still goes out
 * while the page unloads. MeetingApi (the public /m/<code> page) sends the meeting token instead, or the session's
 * token where it matters (link info and joining as a member). Mock mode gets a local-only fake (no server, no media).
 */
import type {
  CaptionResponse,
  ChatResponse,
  Huddle,
  HuddleRecordResponse,
  HuddleResponse,
  HuddlesOverviewResponse,
  JoinHuddleRequest,
  JoinHuddleResponse,
  ListMeetingsResponse,
  Meeting,
  MeetingResponse,
  MeetingStateResponse,
  PublicMeeting,
  RevokeMeetingsResponse,
  UpdateHuddleRequest,
} from "@bundle/contracts";
import { buildPath, routes, type RouteName } from "@bundle/contracts/routes";
import { ApiFailure, type ApiErrorKind, type HuddleApi, type MeetingApi, type SessionViewModel } from "@bundle/core";
import { API_BASE } from "../env.ts";

function kindFor(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  if (status === 429) return "rateLimited";
  if (status >= 400 && status < 500) return "invalid";
  return status === 502 || status === 503 || status === 504 ? "network" : "server";
}

/** A fresh access token for media requests too (the SFU proxy needs the same bearer token). */
export async function freshToken(session: SessionViewModel): Promise<string | undefined> {
  await session.ensureFreshToken();
  return session.state.session?.token;
}

type Call = { body?: unknown; keepalive?: boolean; query?: string };

async function send(url: string, method: string, token: string | undefined, o: Call): Promise<Response> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (o.body !== undefined) headers["content-type"] = "application/json";
  if (token !== undefined) headers.authorization = `Bearer ${token}`;
  try {
    return await fetch(url, { method, headers, body: o.body === undefined ? undefined : JSON.stringify(o.body), keepalive: o.keepalive ?? false });
  } catch (e) {
    throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
  }
}

async function read<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown;
  try {
    json = text === "" ? undefined : JSON.parse(text);
  } catch {
    json = undefined;
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string } } | undefined)?.error;
    throw new ApiFailure(kindFor(res.status), res.status, err?.code ?? String(res.status), err?.message ?? res.statusText);
  }
  return json as T;
}

/** A request with the session's token (refreshed first, and retried once after a 401). */
async function withSession<T>(session: SessionViewModel, base: string, name: RouteName, params: Record<string, string>, o: Call = {}): Promise<T> {
  const route = routes[name];
  const url = base + buildPath(route.path, params) + (o.query ?? "");
  let token = await freshToken(session);
  let res = await send(url, route.method, token, o);
  if (res.status === 401 && token !== undefined && (await session.authRejected(token))) {
    token = session.state.session?.token;
    res = await send(url, route.method, token, o);
  }
  return read<T>(res);
}

export function createHuddleApi(session: SessionViewModel, base: string = API_BASE): HuddleApi {
  const call = <T>(name: RouteName, params: Record<string, string>, body?: unknown, keepalive = false, query = ""): Promise<T> =>
    withSession<T>(session, base, name, params, { body, keepalive, query });

  return {
    join: (ws, channelId, req) => call<JoinHuddleResponse>("joinHuddle", { ws, channelId }, req),
    joinMeeting: (ws, code, req) => call<JoinHuddleResponse>("joinMeeting", { ws, code }, req),
    overview: (ws) => call<HuddlesOverviewResponse>("listHuddles", { ws }),
    record: (ws, huddleId) => call<HuddleRecordResponse>("getHuddleRecord", { ws, huddleId }),
    revokeMeetings: async (ws) => (await call<RevokeMeetingsResponse>("revokeMeetings", { ws })).revoked,
    update: async (ws, huddleId, req) => (await call<HuddleResponse>("updateHuddle", { ws, huddleId }, req)).huddle,
    heartbeat: (ws, huddleId) => call<void>("huddleHeartbeat", { ws, huddleId }),
    leave: (ws, huddleId) => call<void>("leaveHuddle", { ws, huddleId }, undefined, true),
    createMeeting: async (ws, req) => (await call<MeetingResponse>("createMeeting", { ws }, req)).meeting,
    updateMeeting: async (ws, code, req) => (await call<MeetingResponse>("updateMeeting", { ws, code }, req)).meeting,
    shareLink: async (ws, huddleId) => (await call<MeetingResponse>("shareHuddle", { ws, huddleId })).meeting,
    decideLobby: async (ws, huddleId, req) => (await call<HuddleResponse>("huddleLobby", { ws, huddleId }, req)).huddle,
    moderate: (ws, huddleId, req) => call<void>("moderateHuddle", { ws, huddleId }, req),
    chat: async (ws, huddleId, after) => (await call<ChatResponse>("huddleChat", { ws, huddleId }, undefined, false, `?after=${after}`)).messages,
    postChat: async (ws, huddleId, text) => (await call<ChatResponse>("postHuddleChat", { ws, huddleId }, { text })).messages[0]!,
    ring: (ws, huddleId, userIds) => call<void>("ringHuddle", { ws, huddleId }, { userIds }),
    decline: (ws, huddleId, callerId) => call<void>("declineHuddle", { ws, huddleId }, { callerId }),
    caption: async (ws, huddleId, text) => (await call<CaptionResponse>("postHuddleCaption", { ws, huddleId }, { text })).caption,
    async captionAudio(ws, huddleId, clip, mime) {
      // Raw audio body (not JSON): its own request with the session's token.
      const url = base + buildPath(routes.postHuddleCaptionAudio.path, { ws, huddleId });
      const token = await freshToken(session);
      let res: Response;
      try {
        res = await fetch(url, { method: "POST", headers: { "content-type": mime, accept: "application/json", ...(token === undefined ? {} : { authorization: `Bearer ${token}` }) }, body: clip as Blob });
      } catch (e) {
        throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
      }
      return (await read<CaptionResponse>(res)).caption;
    },
    postRecording: async (ws, huddleId, req) => {
      await call<unknown>("postHuddleRecording", { ws, huddleId }, req);
    },
  };
}

/**
 * The public meeting page's API. `session` is set when signed in on this device: the link info then says whether I'm a
 * member of the host's workspace, and joining as a member goes with my session token. Everything after joining uses
 * the meeting token the server handed out.
 */
export function createMeetingApi(session: SessionViewModel | undefined, base: string = API_BASE): MeetingApi {
  const url = (name: RouteName, code: string, query = "") => base + buildPath(routes[name].path, { code }) + query;
  const withToken = async <T>(name: RouteName, code: string, token: string, o: Call = {}): Promise<T> =>
    read<T>(await send(url(name, code, o.query), routes[name].method, token, o));
  const signedIn = () => session !== undefined && session.state.session !== undefined;
  return {
    info: async (code) => {
      if (session !== undefined && signedIn()) {
        try {
          return await withSession<PublicMeeting>(session, base, "meetingInfo", { code });
        } catch (e) {
          // A stale session shouldn't hide the page: ask again as anyone.
          if (!(e instanceof ApiFailure) || e.kind !== "auth") throw e;
        }
      }
      return read<PublicMeeting>(await send(url("meetingInfo", code), routes.meetingInfo.method, undefined, {}));
    },
    join: async (code, req, asMember) =>
      asMember && session !== undefined
        ? withSession<MeetingStateResponse>(session, base, "meetingJoin", { code }, { body: req })
        : read<MeetingStateResponse>(await send(url("meetingJoin", code), routes.meetingJoin.method, undefined, { body: req })),
    state: (code, token, after) => withToken<MeetingStateResponse>("meetingState", code, token, { query: `?after=${after}` }),
    update: async (code, token, req) => (await withToken<HuddleResponse>("meetingUpdateMe", code, token, { body: req })).huddle,
    leave: (code, token) => withToken<void>("meetingLeave", code, token, { keepalive: true }),
    chat: async (code, token, text) => (await withToken<ChatResponse>("meetingChat", code, token, { body: { text } })).messages[0]!,
    lobby: async (code, token, req) => (await withToken<HuddleResponse>("meetingLobby", code, token, { body: req })).huddle,
    moderate: (code, token, req) => withToken<void>("meetingModerate", code, token, { body: req }),
  };
}

/** Meeting links I host (not on the HuddleApi port yet: the web's "New external huddle" sheet lists them). */
export async function listMeetings(api: { list: () => Promise<ListMeetingsResponse> }): Promise<ListMeetingsResponse["meetings"]> {
  return (await api.list()).meetings;
}

/** Mock mode (`?mock`): a huddle with just me, no media; links and the huddles I left (Recent) live in memory. */
export function createMockHuddleApi(meId: () => string): HuddleApi {
  let current: Huddle | undefined;
  const links: Meeting[] = [];
  const past: HuddleRecordResponse[] = [];
  let said: HuddleRecordResponse["transcript"] = [];
  let wrote: HuddleRecordResponse["chatMessages"] = [];
  const me = (req: JoinHuddleRequest) => ({ userId: meId(), joinedAt: Date.now(), muted: req.muted ?? false, video: req.video ?? false, screen: false, tracks: {} });
  const join = async (channelId: string, req: JoinHuddleRequest): Promise<JoinHuddleResponse> => {
    current = { id: `mock-${channelId}`, channelId, startedBy: meId(), startedAt: Date.now(), participants: [me(req)], version: 1 };
    const link = links.find((m) => `meeting:${m.code}` === channelId);
    if (link !== undefined) current.meeting = { code: link.code, title: link.title, hostId: link.hostId, policy: link.policy, locked: false };
    return { huddle: current, media: { kind: "unavailable" } };
  };
  const letters = () => Array.from({ length: 10 }, () => String.fromCharCode(97 + Math.floor(Math.random() * 26))).join("");
  return {
    join: (_ws, channelId, req) => join(channelId, req),
    joinMeeting: (_ws, code, req) => join(`meeting:${code}`, req),
    async overview() {
      return { live: current === undefined ? [] : [current], links: [...links], recent: past.map((p) => p.record) };
    },
    async record(_ws, huddleId) {
      const found = past.find((p) => p.record.huddleId === huddleId);
      if (found === undefined) throw new ApiFailure("notFound", 404, "not_found", "No huddle");
      return found;
    },
    async revokeMeetings() {
      const n = links.filter((m) => !m.revoked).length;
      links.splice(0, links.length, ...links.map((m) => ({ ...m, revoked: true })));
      return n;
    },
    async update(_ws, _id, req: UpdateHuddleRequest) {
      if (current === undefined) throw new ApiFailure("notFound", 404, "not_found", "No huddle");
      const p = current.participants[0]!;
      const next = { ...p };
      if (req.muted !== undefined) next.muted = req.muted;
      if (req.video !== undefined) next.video = req.video;
      if (req.screen !== undefined) next.screen = req.screen;
      if (req.hand !== undefined) {
        if (req.hand) next.handRaisedAt = Date.now();
        else delete next.handRaisedAt;
      }
      if (req.tracks !== undefined) next.tracks = req.tracks;
      if (req.reaction !== undefined) next.reaction = { emoji: req.reaction, at: Date.now() };
      if (req.captions !== undefined) next.captions = req.captions;
      if (req.recording === true) current = { ...current, recording: { userId: meId(), startedAt: Date.now() } };
      if (req.recording === false && current.recording !== undefined) {
        const { recording: _gone, ...rest } = current;
        current = rest;
      }
      current = { ...current, participants: [next], version: current.version + 1 };
      return current;
    },
    async heartbeat() {},
    async leave() {
      if (current !== undefined) {
        const record: HuddleRecordResponse["record"] = {
          huddleId: current.id,
          channelId: current.channelId,
          startedBy: current.startedBy,
          startedAt: current.startedAt,
          endedAt: Date.now(),
          participantIds: [meId()],
          guests: [],
          transcript: said.length > 0,
          chat: wrote.length > 0,
          notes: false,
        };
        if (current.meeting !== undefined) Object.assign(record, { title: current.meeting.title, code: current.meeting.code });
        past.unshift({ record, transcript: said, chatMessages: wrote });
      }
      current = undefined;
      said = [];
      wrote = [];
    },
    async createMeeting(_ws, req) {
      const s = letters();
      const code = `${s.slice(0, 3)}-${s.slice(3, 7)}-${s.slice(7)}`;
      const m: Meeting = { code, url: `${location.origin}/m/${code}`, title: req.title, huddleId: `mock-h-${code}`, hostId: meId(), policy: req.policy ?? "ask", locked: false, revoked: false, createdAt: Date.now() };
      if (req.scheduledAt !== undefined) m.scheduledAt = req.scheduledAt;
      if (req.invitees !== undefined) m.invitees = req.invitees;
      links.unshift(m);
      return m;
    },
    async updateMeeting(_ws, code, req) {
      const i = links.findIndex((m) => m.code === code);
      if (i < 0) throw new ApiFailure("notFound", 404, "not_found", "No link");
      const m = { ...links[i]! };
      if (req.title !== undefined) m.title = req.title;
      if (req.policy !== undefined) m.policy = req.policy;
      if (req.revoked === true) m.revoked = true;
      links[i] = m;
      return m;
    },
    shareLink: () => Promise.reject(new ApiFailure("forbidden", 403, "forbidden", "Not in mock mode")),
    async decideLobby() {
      if (current === undefined) throw new ApiFailure("notFound", 404, "not_found", "No huddle");
      return current;
    },
    async moderate() {},
    async chat() {
      return [];
    },
    async ring() {
      // Mock mode: nobody else to ring.
    },
    async decline() {},
    // Mock mode: my own phrases come straight back as captions (no server, nobody else).
    async caption(_ws, huddleId, text) {
      const caption = { id: `cap-${Date.now()}`, huddleId, channelId: current?.channelId ?? "", userId: meId(), name: "", text, ts: Date.now() };
      said = [...said, { userId: caption.userId, name: caption.name, text, ts: caption.ts }];
      return caption;
    },
    async postRecording() {},
    async postChat(_ws, _id, text) {
      const m = { seq: Date.now(), authorId: meId(), authorName: "", external: false, text, ts: Date.now() };
      wrote = [...wrote, m];
      return m;
    },
  };
}
